import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../auth/AuthComponents';

function formatTime(value) {
  const d = new Date(value);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const oneDay = 86400000;
  if (diff < oneDay && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (diff < 7 * oneDay) {
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiRequest('notifications?limit=20&unread_only=true');
      setItems(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // silent
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    void load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [user, load]);

  const busy = clearing || actingId != null;

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    await load();
    setLoading(false);
  };

  const handleClick = async (item) => {
    if (busy) return;
    setActingId(item.id);
    try {
      if (!item.is_read) {
        await apiRequest(`notifications/${item.id}/read`, { method: 'PUT' });
      }
      setItems((prev) => prev.filter((n) => n.id !== item.id));
      setUnreadCount((prev) => Math.max(0, prev - (item.is_read ? 0 : 1)));
      setOpen(false);
      if (item.link) navigate(item.link);
    } catch {
      // silent
    } finally {
      setActingId(null);
    }
  };

  const clearAll = async () => {
    if (busy) return;
    setClearing(true);
    try {
      await apiRequest('notifications/read-all', { method: 'PUT' });
      setItems([]);
      setUnreadCount(0);
    } catch {
      // silent
    } finally {
      setClearing(false);
    }
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : handleOpen())}
        className="relative p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="elevated-card fixed left-3 right-3 top-16 z-50 max-h-[70vh] overflow-hidden rounded-xl shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 sm:max-h-96">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
              <span className="text-sm font-semibold text-[var(--color-text)]">Notifications</span>
              <div className="flex items-center gap-3">
                {items.length > 0 ? (
                  <button
                    type="button"
                    onClick={clearAll}
                    disabled={busy}
                    className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    {clearing ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Clearing…
                      </>
                    ) : (
                      'Clear all'
                    )}
                  </button>
                ) : null}
                <button type="button" onClick={() => setOpen(false)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading ? (
                <p className="p-4 text-sm text-[var(--color-text-muted)]">Loading…</p>
              ) : items.length === 0 ? (
                <p className="p-4 text-sm text-[var(--color-text-muted)]">No notifications yet.</p>
              ) : (
                items.map((item) => {
                  const isActing = actingId === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleClick(item)}
                      disabled={busy}
                      className={`w-full text-left px-4 py-3 border-b border-[var(--color-border)] hover:bg-[var(--color-surface-muted)]/60 transition-colors disabled:opacity-60 ${
                        !item.is_read ? 'bg-[var(--color-primary-soft)]' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-[var(--color-text)] flex-1">{item.title}</p>
                        {!item.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[var(--color-primary)] shrink-0" />}
                      </div>
                      <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">{item.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        {(item.type === 'lawyer_inquiry' || item.type === 'conversation_message') && item.link ? (
                          isActing ? (
                            <span className="text-xs text-[var(--color-primary)] font-semibold inline-flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Opening…
                            </span>
                          ) : (
                            <span className="text-xs text-[var(--color-primary)] font-semibold">View message →</span>
                          )
                        ) : isActing ? (
                          <span className="text-xs text-[var(--color-primary)] font-semibold inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Opening…
                          </span>
                        ) : (
                          <span />
                        )}
                        <span className="text-[10px] text-[var(--color-text-muted)]">{formatTime(item.created_at)}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
